import type { Vec2 } from "./geometry";
import type { EntityComponent } from "./components";
import type { EntityId, EntityKind } from "./Entity";

export const SCENE_FORMAT = "kurio.scene";
export const CURRENT_SCENE_VERSION = 1;

export interface EntityDefinition {
  readonly id: EntityId;
  readonly definitionId: string;
  readonly kind?: EntityKind;
  readonly asset?: string;
  readonly position?: Vec2;
  readonly rotation?: number;
  readonly zIndex?: number;
  readonly components?: readonly EntityComponent[];
  readonly hidden?: boolean;
}

export interface SecretDefinition {
  readonly id: string;
  readonly condition: { readonly type: string; readonly [key: string]: unknown };
  readonly actions: readonly { readonly type: string; readonly [key: string]: unknown }[];
}

export interface TerrainPaletteEntry {
  readonly color: readonly [number, number, number];
  readonly walkable?: boolean;
}

export interface TerrainLayer {
  readonly id: string;
  readonly data: readonly string[];
}

export interface TerrainDefinition {
  readonly tileSize: number;
  readonly palette: Readonly<Record<string, TerrainPaletteEntry>>;
  readonly layers: readonly TerrainLayer[];
}

export interface SceneDefinition {
  readonly format: typeof SCENE_FORMAT;
  readonly version: number;
  readonly id: string;
  readonly title?: string;
  readonly size: { readonly width: number; readonly height: number };
  readonly background: string;
  readonly entities: readonly EntityDefinition[];
  readonly secrets?: readonly SecretDefinition[];
  readonly terrainFile: string;
}

export class SceneValidationError extends Error {}

export function validateSceneDefinition(data: unknown): SceneDefinition {
  if (typeof data !== "object" || data === null) {
    throw new SceneValidationError("Scene definition must be an object");
  }
  const d = data as Record<string, unknown>;
  if (d["format"] !== SCENE_FORMAT) {
    throw new SceneValidationError(`Expected format "${SCENE_FORMAT}", got "${String(d["format"])}"`);
  }
  if (typeof d["version"] !== "number") {
    throw new SceneValidationError("Scene definition missing numeric version");
  }
  if (typeof d["id"] !== "string" || d["id"].length === 0) {
    throw new SceneValidationError("Scene definition missing id");
  }
  const size = d["size"] as { width?: unknown; height?: unknown } | undefined;
  if (!size || typeof size.width !== "number" || typeof size.height !== "number") {
    throw new SceneValidationError("Scene definition missing numeric size.width/size.height");
  }
  if (typeof d["background"] !== "string") {
    throw new SceneValidationError("Scene definition missing background");
  }
  if (!Array.isArray(d["entities"])) {
    throw new SceneValidationError("Scene definition missing entities array");
  }
  if (typeof d["terrainFile"] !== "string" || d["terrainFile"].length === 0) {
    throw new SceneValidationError("Scene definition missing non-empty terrainFile");
  }
  for (const entity of d["entities"] as unknown[]) {
    if (typeof entity !== "object" || entity === null) {
      throw new SceneValidationError("Entity definitions must be objects");
    }
    const e = entity as Record<string, unknown>;
    if (typeof e["id"] !== "string" || typeof e["definitionId"] !== "string") {
      throw new SceneValidationError(`Entity definition missing id or definitionId: ${JSON.stringify(entity)}`);
    }
  }
  return data as SceneDefinition;
}

/** Migrates an on-disk scene definition of any known version to {@link CURRENT_SCENE_VERSION}. */
export function migrateSceneDefinition(data: SceneDefinition): SceneDefinition {
  // No prior versions exist yet; this is the seam future migrations hang off.
  if (data.version === CURRENT_SCENE_VERSION) return data;
  throw new SceneValidationError(`Unsupported scene version: ${data.version}`);
}
